import React, { useState, useRef, useCallback } from 'react'
import type { Block } from '../../editor/types'

interface CompareBlockProps {
  block: Block
}

function getConfig(block: Block) {
  const config = (block.config || {}) as Record<string, unknown>
  return {
    beforeUrl: (config.beforeUrl as string) || '',
    afterUrl: (config.afterUrl as string) || '',
    beforeLabel: (config.beforeLabel as string) || 'Before',
    afterLabel: (config.afterLabel as string) || 'After',
    showLabels: config.showLabels !== false,
    orientation: (config.orientation as 'horizontal' | 'vertical') || 'horizontal',
    initialPosition: (config.initialPosition as number) ?? 50,
    aspectRatio: (config.aspectRatio as string) || '16/10',
  }
}

export default function CompareBlock({ block }: CompareBlockProps) {
  const { beforeUrl, afterUrl, beforeLabel, afterLabel, showLabels, orientation, initialPosition, aspectRatio } = getConfig(block)
  const [position, setPosition] = useState(initialPosition)
  const containerRef = useRef<HTMLDivElement>(null)
  const dragging = useRef(false)

  const updatePosition = useCallback((clientX: number, clientY: number) => {
    if (!containerRef.current) return
    const rect = containerRef.current.getBoundingClientRect()
    if (orientation === 'horizontal') {
      const pct = ((clientX - rect.left) / rect.width) * 100
      setPosition(Math.max(0, Math.min(100, pct)))
    } else {
      const pct = ((clientY - rect.top) / rect.height) * 100
      setPosition(Math.max(0, Math.min(100, pct)))
    }
  }, [orientation])

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    dragging.current = true
    ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
    updatePosition(e.clientX, e.clientY)
  }, [updatePosition])

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (!dragging.current) return
    updatePosition(e.clientX, e.clientY)
  }, [updatePosition])

  const onPointerUp = useCallback(() => {
    dragging.current = false
  }, [])

  if (!beforeUrl && !afterUrl) {
    return (
      <div className="h-48 bg-canvas rounded-lg flex items-center justify-center text-text-secondary text-sm">
        Set before & after images in properties
      </div>
    )
  }

  const isH = orientation === 'horizontal'
  const accent = block.accent || 'var(--accent, #5048e5)'

  return (
    <div
      ref={containerRef}
      className="relative w-full overflow-hidden rounded-lg select-none"
      style={{ aspectRatio, cursor: orientation === 'horizontal' ? 'col-resize' : 'row-resize' }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
    >
      {/* Before image — clipped to the right of the slider (revealed by sliding left) */}
      {beforeUrl && (
        <div
          className="absolute inset-0"
          style={{
            clipPath: isH
              ? `inset(0 0 0 ${position}%)`
              : `inset(${position}% 0 0 0)`,
          }}
        >
          <img src={beforeUrl} alt={beforeLabel} className="w-full h-full object-cover object-top" draggable={false} />
        </div>
      )}

      {/* After image — clipped to the left of the slider (revealed by sliding right) */}
      {afterUrl && (
        <div
          className="absolute inset-0"
          style={{
            clipPath: isH
              ? `inset(0 ${100 - position}% 0 0)`
              : `inset(0 0 ${100 - position}% 0)`,
          }}
        >
          <img src={afterUrl} alt={afterLabel} className="w-full h-full object-cover object-top" draggable={false} />
        </div>
      )}

      {/* Slider line */}
      <div
        className="absolute z-10"
        style={isH
          ? { left: `${position}%`, top: 0, bottom: 0, width: '3px', transform: 'translateX(-50%)' }
          : { top: `${position}%`, left: 0, right: 0, height: '3px', transform: 'translateY(-50%)' }
        }
      >
        <div className="w-full h-full" style={{ background: accent }} />
      </div>

      {/* Handle */}
      <div
        className="absolute z-20 flex items-center justify-center"
        style={isH
          ? { left: `${position}%`, top: '50%', transform: 'translate(-50%, -50%)' }
          : { top: `${position}%`, left: '50%', transform: 'translate(-50%, -50%)' }
        }
      >
        <div
          className="w-10 h-10 rounded-full shadow-lg flex items-center justify-center"
          style={{ background: accent, color: 'white' }}
        >
          {isH ? (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M4 8L6.5 5.5M4 8L6.5 10.5M4 8H12M12 8L9.5 5.5M12 8L9.5 10.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M8 4L5.5 6.5M8 4L10.5 6.5M8 4V12M8 12L5.5 9.5M8 12L10.5 9.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )}
        </div>
      </div>

      {/* Labels */}
      {showLabels && (
        <>
          <div className="absolute top-3 left-3 z-10 bg-black/50 backdrop-blur-sm text-white text-xs font-bold px-2.5 py-1 rounded-md pointer-events-none">
            {beforeLabel}
          </div>
          <div className="absolute top-3 right-3 z-10 bg-black/50 backdrop-blur-sm text-white text-xs font-bold px-2.5 py-1 rounded-md pointer-events-none">
            {afterLabel}
          </div>
        </>
      )}
    </div>
  )
}
